import type { Currency } from '@ps/web-core-enums';
import { ErrorCode, FeatureFlag, ResolverTag } from '@ps/web-core-enums';
import { type Response, useFeature } from '@ps/web-core-store';
import { StarsButton as Button, InfoIcon, Translation } from '@ps/web-shared-components';
import { StarsInfo, StarsLink, createForm } from '@ps/web-shared-ui';
import { maxNumber, minNumber, numbersOnly } from '@ps/web-shared-validators';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import CurrencyAmount from '@swf/components/CurrencyAmount/CurrencyAmount';
import ResolverLink from '@swf/components/ResolverLink/ResolverLink';
import useRequest from '@swf/core/hooks/useRequest';
import { type Dispatch, select } from '@swf/core/store';
import { AmountCurrency } from '@swf/first';

import sharedStyles from '../../styles/styles.scss';

import styles from './CrossOperatorDepositLimit.scss';
import {
  TXTCLI_Confirm,
  TXTCLI_Cross_Operator_Deposit_Limit,
  TXTCLI_OK,
  TXTCLI_Something_went_wrongX,
  TXTWCL_Due_to_regulationsX_you_can_on_ELL,
  TXTWCL_If_you_already_have_a_Cross_Op_ELL,
  TXTWCL_Please_set_up_the_maximum_amou_ELL,
  TXTWCL_Since_youXve_been_inactive_for_ELL,
  TXTWCL_The_minimum_value_for_this_lim_ELL,
  TXTWCL_We_couldnXt_process_your_reque_ELL_1,
  TXTWCL_Whole_numbers_onlyX_No_decimal_ELL,
  TXTWCL_You_need_to_set_your_Cross_Ope_ELL
} from './CrossOperatorDepositLimitForm.messages.json';

export interface FormValues {
  amount: number;
  currency: Currency;
  skip?: boolean;
}

interface Props {
  setLimitSuccess?: (result: boolean) => void;
  setLoading?: (loading: boolean) => void;
  loading?: boolean;
  restrictedWidth?: boolean;
}

const Form = createForm<FormValues>();

const CrossOperatorDepositLimitForm: React.FC<Props> = ({
  loading,
  setLoading,
  setLimitSuccess,
  restrictedWidth = false
}) => {
  const currency = useSelector(select.account.getCurrency);
  const deLugasRegistration = useFeature(FeatureFlag.DeLugasReregistration);
  const request = useRequest();
  const [limitRequired, setLimitRequired] = useState<boolean>(false);
  const [formError, setFormError] = useState<boolean>(false);
  const {
    account: { updateLugasCrossOperatorDepositLimit }
  } = useDispatch<Dispatch>();

  const isEmbedded = useSelector(select.device.isEmbedded);
  const { modal } = useDispatch<Dispatch>();

  useEffect(() => {
    if (deLugasRegistration) {
      request({ service: '/api/v1-preview/account/lugas-registration', method: 'DELETE' });
    }
  }, [deLugasRegistration, request]);

  const handleSubmit = async (values: FormValues) => {
    let response: Response;
    setLoading?.(true);

    if (values.skip) {
      response = await updateLugasCrossOperatorDepositLimit({});
    } else {
      response = await updateLugasCrossOperatorDepositLimit({
        limitAmount: Number(values.amount) * 100
      });
    }

    if (response && response.success) {
      setLimitSuccess?.(true);
      if (isEmbedded) {
        if (!setLimitSuccess) window.close();
      } else {
        modal.close('/notifications/cross-operator-deposit-limit');
      }

      return;
    }

    setLoading?.(false);
    if (response?.errors?.code === ErrorCode.ERR_LUGAS_LIMIT_REQUIRED) {
      setFormError(false);
      return setLimitRequired(true);
    }

    setLimitRequired(false);
    setFormError(true);
  };

  return (
    <>
      {deLugasRegistration ? (
        <Form
          onSubmit={handleSubmit}
          render={({ form }) => {
            const { submitting, pristine, valid } = form.getState();

            return (
              <div className={restrictedWidth ? styles.restrictedWidth : ''}>
                {limitRequired && (
                  <StarsInfo type="error" className={styles.starsInfoBlock}>
                    <InfoIcon />
                    <Translation {...TXTWCL_You_need_to_set_your_Cross_Ope_ELL} />
                  </StarsInfo>
                )}
                {formError && (
                  <StarsInfo type="error" className={styles.starsInfoBlock}>
                    <InfoIcon />
                    <div>
                      <b>
                        <Translation tagName="p" {...TXTCLI_Something_went_wrongX} />
                      </b>
                      <Translation
                        tagName="p"
                        {...TXTWCL_We_couldnXt_process_your_reque_ELL_1}
                        values={{
                          a: (msg: React.ReactNode) => (
                            <ResolverLink resolverTag={ResolverTag.HelpCenter}>{msg}</ResolverLink>
                          )
                        }}
                      />
                    </div>
                  </StarsInfo>
                )}
                <Translation tagName="p" {...TXTWCL_Since_youXve_been_inactive_for_ELL} />
                <Translation tagName="p" {...TXTWCL_Please_set_up_the_maximum_amou_ELL} />
                <AmountCurrency
                  defaultCurrency={currency as Currency}
                  includeCurrencies={[currency as Currency]}
                  label={TXTCLI_Cross_Operator_Deposit_Limit}
                  normalize={false}
                  values={{ 0: 24 }}
                  currencyFieldName="currency"
                  amountFieldName="amount"
                  validators={[
                    numbersOnly(TXTWCL_Whole_numbers_onlyX_No_decimal_ELL, true),
                    minNumber(
                      1,
                      { ...TXTWCL_The_minimum_value_for_this_lim_ELL, values: { 0: 1 } },
                      false
                    ),
                    maxNumber(
                      1000,
                      {
                        ...TXTWCL_Due_to_regulationsX_you_can_on_ELL,
                        values: {
                          0: <CurrencyAmount value={100000} currency={currency} />
                        }
                      },
                      false
                    )
                  ]}
                />
                {!limitRequired && (
                  <Translation
                    tagName="p"
                    {...TXTWCL_If_you_already_have_a_Cross_Op_ELL}
                    values={{
                      a: (msg: React.ReactNode) => (
                        <StarsLink
                          onClick={(event) => {
                            event.preventDefault();
                            handleSubmit({ amount: 0, currency: currency as Currency, skip: true });
                          }}
                        >
                          {msg}
                        </StarsLink>
                      )
                    }}
                  />
                )}
                <Button
                  type="submit"
                  className={sharedStyles.submitBtn}
                  disabled={pristine || !valid}
                  loading={submitting || loading}
                  translation={TXTCLI_Confirm}
                />{' '}
              </div>
            );
          }}
        />
      ) : (
        <>
          <Translation {...TXTCLI_Something_went_wrongX} />
          <Button
            type="submit"
            className={sharedStyles.submitBtn}
            onClick={() => modal.close('/notifications/cross-operator-deposit-limit')}
            translation={TXTCLI_OK}
          />
        </>
      )}
    </>
  );
};

export default CrossOperatorDepositLimitForm;
